import Script from "next/script";
import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import constants from "@/global/config/constants";

import type { TeamConfig } from "../types";

export const purchaseSeatsLanguages = {
  similarSeatModalBody: (seatTitle: string) => (
    <>
      We are going to apply the same or similar seats to all games in your plan.
      Seat locations will be visible on the checkout page.
    </>
  ),
};

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Select Your Plan",
    description: "Select the plan you wish to purchase.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect to your Albuquerque Isotopes account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description:
      "Select the seats you wish to purchase for the games. The same number of seats will be applied across the 4 All-Star Family games. The number of seats you select will reflect the number of ticket packages you purchase.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Isotopes{" "}
        <Link
          href={"https://am.ticketmaster.com/isotopes/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Account Manager
        </Link>
        . Reach out to Live Fan Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How does the All-Star Family Plan work?",
    description:
      "Get a Box Level ticket to each of our 4 All-Star Family games this season with the All-Star Family Plan. Simply log into your Albuquerque Isotopes Account Manager and select your seats.",
  },
  {
    title: "Do I have to select all my games now?",
    description:
      "Yes - all 4 games will automatically be added to your cart when you select the All-Star Family Plan.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description:
      "Yes, you can choose from all currently available inventory in the Box Level. The same number of seats for every game will automatically be applied.",
  },
  {
    title: "Where are event tickets delivered?",
    description: (
      <>
        Tickets will be delivered directly within your Albuquerque Isotopes{" "}
        <Link
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
          href={"https://am.ticketmaster.com/isotopes/"}
        >
          Account Manager
        </Link>
        .
      </>
    ),
  },
];

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Script
        async
        type="text/javascript"
        nonce={process?.env?.ttdPixelScriptNonce}
        src="https://js.adsrvr.org/up_loader.1.1.0.js"
        onLoad={() => {
          console.log("TTD base library loaded");
        }}
        onError={(e) => {
          console.error("Failed to load TTD base library:", e);
        }}
      />

      <Script
        async
        type="text/javascript"
        id={`${teamSchema}-ttd-script`}
        nonce={process?.env?.ttdPixelScriptNonce}
        dangerouslySetInnerHTML={{
          __html: `
            function initTTDPixel() {
              if (typeof TTDUniversalPixelApi === 'function') {
                var universalPixelApi = new TTDUniversalPixelApi();
                window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function (e) {
                  try {
                    const { cartRes, checkoutRes } = e.detail;

                    universalPixelApi.init("tf5i8g0", ["2oamxvb"], "https://insight.adsrvr.org/track/up", {
                      td1: cartRes.totalSeats,
                      td2: checkoutRes.orderNumber,
                      v: String(cartRes.allInPrice),
                    });

                    console.log("ttd pixel event fired");
                  } catch (error) {
                    console.log("ttd pixel event error:", error);
                  }
                });
              } else {
                console.log("TTDUniversalPixelApi not found, retrying in 1 second");
                setTimeout(initTTDPixel, 1000);
              }
            }

            // Wait for DOM to be ready
            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', initTTDPixel);
            } else {
              initTTDPixel();
            }
          `,
        }}
      />
    </>
  );
};
